/**
 * CombatLogFormatter — pure event → display-string.
 *
 * Single rendering surface for every WoW-style combat-log line. Pure
 * functions, no DOM / no React / no chatHandler — easy to unit-test
 * (table-driven below in CombatLogFormatter.test.ts) and reusable from
 * any consumer that needs the same canonical line text (chat bridge,
 * killfeed, log export, replay viewer).
 *
 * Style choices, fixed across all kinds:
 *   - Names wrapped in `[…]` (WoW chat-frame combat-log convention)
 *   - Crits ALL CAPS verb ("CRITICAL hit") so they pop without color
 *   - Schools capitalized ("Physical", "Fire") — readability over
 *     terseness
 *   - Viewer's own actor renders as `[you]` (lowercase) when
 *     `viewerSid` matches — same first-person pivot WoW uses
 *   - `(×N)` suffix appended when `event.multiplier > 1` so coalesced
 *     auto-attack spam doesn't drown the panel
 *
 * Why pure functions instead of a class:
 *   - Trivial to test (no setup/teardown; just call → assert)
 *   - Trivial to reuse from server-side ledgers if/when that lands
 *   - Trivial to compose: future "narrate this combat snippet" tools
 *     can map the same event list through different formatter modes
 *     (terse / verbose / log-export / replay) without instance state
 */

import type {
  CombatLogEvent,
  CombatLogDamageEvent,
  CombatLogHealEvent,
  CombatLogKillEvent,
  CombatLogNpcDeathEvent,
  CombatLogCastEvent,
  CombatLogPhaseEvent,
  CombatLogRespawnEvent,
  ViewerSid,
} from './CombatLogEvent';
import type { AbilitySchool } from '../constants/CombatFeedbackConfig';

/** Final formatted line + supporting metadata. */
export interface FormattedCombatLine {
  /** The display text, ready for a chat 'combat' message. */
  text: string;
  /**
   * Suggested color hint. Maps to the existing damageTypeColors palette
   * downstream (consumers can resolve via `COMBAT_FEEDBACK.damageTypeColors`
   * if they want to colorize per school instead of using the chat panel's
   * uniform 'combat' red).
   */
  schoolHint: AbilitySchool | null;
  /** True when the source event was a critical hit. */
  critical: boolean;
}

const SCHOOL_LABEL: Record<AbilitySchool, string> = {
  physical: 'Physical',
  fire: 'Fire',
  frost: 'Frost',
  arcane: 'Arcane',
  shadow: 'Shadow',
  holy: 'Holy',
  nature: 'Nature',
  chaos: 'Chaos',
  various: 'Various',
};

/**
 * Render a name with optional first-person pivot. `[you]` (lowercase)
 * matches WoW's chat-frame combat-log convention; everyone else gets
 * bracketed verbatim.
 */
function renderName(id: string | null, name: string, viewerSid: ViewerSid): string {
  if (id && viewerSid && id === viewerSid) return '[you]';
  // Fallback for bad name resolution — keeps the line shape intact
  // instead of producing `[]` or `[undefined]`.
  const safe = (name && name.length > 0) ? name : '???';
  return `[${safe}]`;
}

/** Append a `(×N)` suffix when N > 1; no-op for singletons. */
function withMultiplier(line: string, multiplier: number): string {
  return multiplier > 1 ? `${line} (×${multiplier})` : line;
}

/**
 * Damage line:
 *   [Brick] hit [Lysa] with [Charge] for 47 Physical
 *   [Brick] CRITICAL [Lysa] with [Fireball] for 89 Fire
 *   [Brick] hit [you] for 31 Physical            ← no ability = bracket-less
 */
function formatDamage(e: CombatLogDamageEvent, viewerSid: ViewerSid): FormattedCombatLine {
  const src = renderName(e.sourceId, e.sourceName, viewerSid);
  const tgt = renderName(e.targetId, e.targetName, viewerSid);
  const verb = e.critical ? 'CRITICAL' : 'hit';
  const ability = e.abilityName ? ` with [${e.abilityName}]` : '';
  const school = SCHOOL_LABEL[e.school] ?? 'Physical';
  const text = withMultiplier(
    `${src} ${verb} ${tgt}${ability} for ${e.amount} ${school}`,
    e.multiplier,
  );
  return { text, schoolHint: e.school, critical: e.critical };
}

/**
 * Heal line (shape only — no producer yet, see CombatLogEvent.ts):
 *   [Cain] healed [you] with [Holy Shock] for 64
 */
function formatHeal(e: CombatLogHealEvent, viewerSid: ViewerSid): FormattedCombatLine {
  const src = renderName(e.sourceId, e.sourceName, viewerSid);
  const tgt = renderName(e.targetId, e.targetName, viewerSid);
  const verb = e.critical ? 'CRITICALLY healed' : 'healed';
  const ability = e.abilityName ? ` with [${e.abilityName}]` : '';
  const overheal = e.overheal > 0 ? ` (${e.overheal} overheal)` : '';
  const text = withMultiplier(
    `${src} ${verb} ${tgt}${ability} for ${e.amount}${overheal}`,
    e.multiplier,
  );
  return { text, schoolHint: 'holy', critical: e.critical };
}

/**
 * Kill line (PvP):
 *   [Cain] killed [you]
 *   [you] killed [Bot Pip]
 */
function formatKill(e: CombatLogKillEvent, viewerSid: ViewerSid): FormattedCombatLine {
  const src = renderName(e.sourceId, e.sourceName, viewerSid);
  const tgt = renderName(e.targetId, e.targetName, viewerSid);
  return {
    text: withMultiplier(`${src} killed ${tgt}`, e.multiplier),
    schoolHint: null,
    critical: false,
  };
}

/**
 * NPC death line:
 *   [Sir Galaheron] was defeated by [you]   (when credited)
 *   [Sir Galaheron] was defeated            (no credit)
 */
function formatNpcDeath(e: CombatLogNpcDeathEvent, viewerSid: ViewerSid): FormattedCombatLine {
  const npc = renderName(e.npcId, e.npcName, viewerSid);
  if (e.defeatedById) {
    const by = renderName(e.defeatedById, e.defeatedByName ?? '???', viewerSid);
    const xp = e.xpGained > 0 ? ` (+${e.xpGained} XP)` : '';
    return {
      text: withMultiplier(`${npc} was defeated by ${by}${xp}`, e.multiplier),
      schoolHint: null,
      critical: false,
    };
  }
  return {
    text: withMultiplier(`${npc} was defeated`, e.multiplier),
    schoolHint: null,
    critical: false,
  };
}

/**
 * Ability cast line:
 *   [Brick] casts [Charge]
 *   [Brick] casts [Charge] at [Lysa]
 */
function formatCast(e: CombatLogCastEvent, viewerSid: ViewerSid): FormattedCombatLine {
  const src = renderName(e.sourceId, e.sourceName, viewerSid);
  const target = e.targetId
    ? ` at ${renderName(e.targetId, e.targetName ?? '???', viewerSid)}`
    : '';
  return {
    text: withMultiplier(`${src} casts [${e.abilityName}]${target}`, e.multiplier),
    schoolHint: null,
    critical: false,
  };
}

/**
 * Boss-phase line:
 *   [Sir Galaheron] entered Phase 2
 */
function formatPhase(e: CombatLogPhaseEvent): FormattedCombatLine {
  return {
    text: `[${e.npcName}] entered ${e.phaseName}`,
    schoolHint: null,
    critical: false,
  };
}

/**
 * Respawn line:
 *   [you] respawned
 *   [Bot Brick] respawned
 */
function formatRespawn(e: CombatLogRespawnEvent, viewerSid: ViewerSid): FormattedCombatLine {
  const tgt = renderName(e.targetId, e.targetName, viewerSid);
  return {
    text: `${tgt} respawned`,
    schoolHint: null,
    critical: false,
  };
}

/**
 * Top-level dispatcher. Add a new branch when extending the union.
 * Exhaustiveness is guarded by the `never` cast at the bottom.
 */
export function formatCombatLogEvent(
  event: CombatLogEvent,
  viewerSid: ViewerSid = null,
): FormattedCombatLine {
  switch (event.kind) {
    case 'damage':     return formatDamage(event, viewerSid);
    case 'heal':       return formatHeal(event, viewerSid);
    case 'kill':       return formatKill(event, viewerSid);
    case 'npc_death':  return formatNpcDeath(event, viewerSid);
    case 'cast':       return formatCast(event, viewerSid);
    case 'phase':      return formatPhase(event);
    case 'respawn':    return formatRespawn(event, viewerSid);
    default: {
      const exhaustive: never = event;
      void exhaustive;
      return { text: '???', schoolHint: null, critical: false };
    }
  }
}
